<template>
  <SectionFrame :section="section" :index="index" :heading="section.title || 'Get in touch'" label="Contact">
    <div class="s2-contact" :data-layout="layout">
      <div class="t-card mo-reveal">
        <p v-if="section.description" class="t-body s2-muted">{{ section.description }}</p>
        <div v-if="sent" class="s2-ok" role="status"><b>Thanks, we've got your message. We'll get back to you soon.</b>
          <p v-if="preview" class="s2-note">This is the studio preview: nothing was sent.</p></div>
        <form v-else class="s2-stack" @submit.prevent="submit" novalidate>
          <div><label class="s2-label" :for="`${uid}-n`">Your name</label><input :id="`${uid}-n`" v-model="name" class="s2-input" autocomplete="name"></div>
          <div><label class="s2-label" :for="`${uid}-c`">Phone or email</label><input :id="`${uid}-c`" v-model="contactField" class="s2-input" autocomplete="email"></div>
          <div><label class="s2-label" :for="`${uid}-m`">Message</label><textarea :id="`${uid}-m`" v-model="message" rows="4" class="s2-input"></textarea></div>
          <div class="s2-hp" aria-hidden="true"><label>Business name<input v-model="honeypot" tabindex="-1" autocomplete="off"></label></div>
          <p v-if="problem || error" class="s2-err" role="alert">{{ problem || error }}</p>
          <button type="submit" class="t-btn" :disabled="sending">{{ sending ? "Sending…" : "Send" }}</button>
        </form>
      </div>
      <aside v-if="layout === 'form-details' && section.showDetails !== false && (phone || email || address)" class="s2-contact-details mo-reveal">
        <p v-if="phone"><span class="s2-muted s2-small">Phone</span><a class="t-link s2-strong" :href="`tel:${phone.replace(/\s/g, '')}`">{{ phone }}</a></p>
        <p v-if="email"><span class="s2-muted s2-small">Email</span><a class="t-link s2-u" :href="`mailto:${email}`">{{ email }}</a></p>
        <p v-if="address"><span class="s2-muted s2-small">Address</span><span>{{ address }}</span></p>
      </aside>
    </div>
  </SectionFrame>
</template>

<script setup>
import {computed, onMounted, ref} from "vue";
import SectionFrame from "./SectionFrame.vue";
import {layoutOf} from "@/utils/sectionLogic.js";
import {useFormPost} from "./useFormPost.js";
import {usePageStore} from "@/stores/pageStore";

const props = defineProps({section: {type: Object, required: true}, index: {type: Number, default: 0}});
const layout = computed(() => layoutOf(props.section, ["form", "form-details"]));
const pageStore = usePageStore();
const phone = computed(() => pageStore.pageData?.businessPhone?.number || "");
const email = computed(() => pageStore.pageData?.businessEmail?.email || pageStore.pageData?.businessEmail?.address || "");
const address = computed(() => {
  const a = pageStore.pageData?.businessAddress;
  return a ? [a.street, a.street2, a.city, a.postcode].filter(Boolean).join(", ") : "";
});
const name = ref(""); const contactField = ref(""); const message = ref(""); const problem = ref("");
const uid = `c${String(props.section.id || "").replace(/[^a-z0-9]/gi, "").slice(0, 8)}`;
const {honeypot, sending, sent, error, preview, post} = useFormPost();

// "Get a quote like this" on a project page sends ?about=…&project=<slug>.
onMounted(() => {
  try {
    const q = new URLSearchParams(window.location.search);
    const about = (q.get("about") || "").slice(0, 120);
    const project = (q.get("project") || "").replace(/[^a-z0-9-]/gi, "").slice(0, 80);
    if (about || project) message.value = [about && `I'd like a quote for: ${about}`, project && `Like your project /showcase/${project}`].filter(Boolean).join("\n");
  } catch (e) { /* no query */ }
});

async function submit() {
  problem.value = "";
  if (name.value.trim().length < 2) { problem.value = "Please add your name."; return; }
  const c = contactField.value.trim();
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(c) && c.replace(/\D/g, "").length < 7) { problem.value = "Please add a phone number or email so we can reply."; return; }
  // Today's contact form endpoint: it lands in Draksy as a lead.
  const page = typeof window !== "undefined" ? window.location.pathname : "";
  await post("contact", {name: name.value.trim(), contact: c, message: `FROM PAGE: ${page}\n\n${message.value}`, requestQuote: false});
}
</script>

<style>
.s2-contact { display: grid; grid-template-columns: minmax(0, 1.3fr) minmax(0, .7fr); gap: var(--t-gap); align-items: start; }
.s2-contact[data-layout="form"] { grid-template-columns: minmax(0, 1fr); max-width: 680px; }
@media (max-width: 760px) { .s2-contact { grid-template-columns: minmax(0, 1fr); } }
.s2-contact-details p { display: flex; flex-direction: column; gap: .15rem; margin: 0 0 1.1rem; }
</style>
